import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Trash2,
  RefreshCw,
  AlertCircle,
  ExternalLink,
} from "lucide-react";
import { PageContainer } from "../components/layout/PageContainer/PageContainer";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { LoadingState } from "../components/composite/LoadingState/LoadingState";
import { EmptyState } from "../components/composite/EmptyState/EmptyState";
import { MessageStrip } from "../components/ui/MessageStrip/MessageStrip";
import { Dialog } from "../components/ui/Dialog/Dialog";
import { useCustomer } from "../contexts/CustomerContext";
import { ROUTES } from "../constants/routes";
import { useCustomerServices } from "../hooks/useCustomerServices";
import customerServiceService from "../services/customerServiceService";

/**
 * ServiceUsage Page
 *
 * Shows services assigned to the selected customer.
 * Allows removing services from the customer.
 * Grouped by category (Apps & PODs, Plugins, APIs).
 */
const ServiceUsage = () => {
  const navigate = useNavigate();
  const { selectedCustomer } = useCustomer();
  const queryClient = useQueryClient();

  // Fetch customer's assigned services using React Query
  const {
    data: services = [],
    isLoading,
    isFetching,
    error: queryError,
    refetch,
  } = useCustomerServices(selectedCustomer?.id);

  // Separate loading states for initial load vs refresh
  const isInitialLoading = isLoading && services.length === 0;
  const isRefreshing = isFetching && !isLoading;

  // Search and filters
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("apps_and_pods");

  // Removal state
  const [isRemoving, setIsRemoving] = useState(false);
  const [serviceToRemove, setServiceToRemove] = useState(null);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [error, setError] = useState(null);

  // Handle query errors
  useEffect(() => {
    if (queryError) {
      setError(queryError.message || "Failed to load assigned services.");
    }
  }, [queryError]);

  // Filter and categorize services
  const filteredAndCategorizedServices = useMemo(() => {
    // First, filter by search query
    let filtered = services.filter((service) => {
      // Search filter (check service ID, name, and description)
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        service.serviceId?.toLowerCase().includes(searchLower) ||
        service.currentVersion?.name?.toLowerCase().includes(searchLower) ||
        service.currentVersion?.description
          ?.toLowerCase()
          .includes(searchLower);

      return matchesSearch;
    });

    // Helper to normalize category for comparison
    const normalizeCategory = (category) => {
      if (!category) return "";
      return category.toLowerCase().replace(/\s+/g, "_");
    };

    // Then categorize by the service's category
    const categorized = {
      apps_and_pods: filtered.filter((s) => {
        const category = normalizeCategory(
          s.currentVersion?.category || s.category || "",
        );
        return category === "apps_and_pods";
      }),
      plugins: filtered.filter((s) => {
        const category = normalizeCategory(
          s.currentVersion?.category || s.category || "",
        );
        return category === "plugins";
      }),
      pod2_plugins: filtered.filter((s) => {
        const category = normalizeCategory(
          s.currentVersion?.category || s.category || "",
        );
        return category === "pod_2.0_plugins_and_actions";
      }),
      apis: filtered.filter((s) => {
        const category = normalizeCategory(
          s.currentVersion?.category || s.category || "",
        );
        return category === "apis";
      }),
    };

    return categorized;
  }, [services, searchQuery]);

  const handleRemoveClick = (service) => {
    setServiceToRemove(service);
    setShowRemoveDialog(true);
  };

  const handleConfirmRemove = async () => {
    if (!serviceToRemove || !selectedCustomer) return;

    setIsRemoving(true);
    setError(null);
    try {
      await customerServiceService.removeService(
        selectedCustomer.id,
        serviceToRemove.serviceId,
      );

      // Invalidate and refetch customer services to update the cache
      queryClient.invalidateQueries({
        queryKey: ["customerServices", selectedCustomer.id],
      });

      setShowRemoveDialog(false);
      setServiceToRemove(null);
    } catch (err) {
      console.error("Failed to remove service:", err);
      setError(err.message || "Failed to remove service.");
    } finally {
      setIsRemoving(false);
    }
  };

  const renderServiceTable = (category) => {
    const categoryServices = filteredAndCategorizedServices[category] || [];

    if (categoryServices.length === 0) {
      return (
        <div className="flex items-center justify-center h-32 border rounded-lg">
          <p className="text-gray-500">
            {searchQuery
              ? "No services match your search."
              : "No services in this category."}
          </p>
        </div>
      );
    }

    return (
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Added On
                </th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {categoryServices.map((service, index) => {
                const currentVersion = service.currentVersion;

                return (
                  <tr
                    key={`${service.serviceId}-${index}`}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-3 py-2">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">
                          {currentVersion?.name || service.serviceId}
                        </span>
                        <span className="text-xs text-gray-500">
                          {service.serviceId}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2 max-w-xl">
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {currentVersion?.description ||
                          "No description available"}
                      </p>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">
                      {new Date(service.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        {currentVersion?.documentation && (
                          <a
                            href={currentVersion.documentation}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800"
                            title="View Documentation"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveClick(service)}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (!selectedCustomer) {
    return (
      <PageContainer
        title="Service Usage"
        description="View and manage customer service assignments"
      >
        <Card className="p-8 text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-orange-500" />
          <h3 className="text-lg font-semibold mb-2">No Customer Selected</h3>
          <p className="text-gray-600 mb-4">
            Please select a customer to view their services.
          </p>
          <Button onClick={() => navigate(ROUTES.SELECT_CUSTOMER)}>
            Select Customer
          </Button>
        </Card>
      </PageContainer>
    );
  }

  return (
    <>
      <PageContainer
        title="Service Usage"
        description={`Service IDs assigned to ${selectedCustomer.name}`}
        actions={
          <div className="flex items-center gap-2 ">
            <button
              onClick={() => refetch()}
              disabled={isRefreshing}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 border borxder-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw
                className={`w-4 h-4 flex-shrink-0 ${
                  isRefreshing ? "animate-spin" : ""
                }`}
              />
              <span className="font-medium text-sm leading-none">Refresh</span>
            </button>
            <button
              onClick={() => navigate(ROUTES.SERVICE_CATALOG)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Plus className="w-4 h-4 flex-shrink-0" />
              <span className="font-medium text-sm leading-none">
                Add Service
              </span>
            </button>
          </div>
        }
      >
        {/* Error Message */}
        {error && (
          <MessageStrip
            type="error"
            className="mb-4"
            onClose={() => setError(null)}
          >
            {error}
          </MessageStrip>
        )}

        {/* Loading State */}
        {isInitialLoading && (
          <LoadingState message="Loading assigned services..." />
        )}

        {/* Empty State */}
        {!isInitialLoading && services.length === 0 && (
          <EmptyState
            icon={<AlertCircle className="w-12 h-12" />}
            title="No Service IDs Assigned"
            description="This customer doesn't have any service IDs assigned yet. Add services from the catalog to get started."
            action={
              <Button onClick={() => navigate(ROUTES.SERVICE_CATALOG)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Services
              </Button>
            }
          />
        )}

        {/* Content */}
        {!isInitialLoading && services.length > 0 && (
          <Card>
            {/* Tabs with integrated filters */}
            <div className="border-b">
              <div className="flex items-center justify-between px-4 py-2">
                <nav className="flex space-x-6" aria-label="Tabs">
                  <button
                    onClick={() => setActiveTab("apps_and_pods")}
                    className={`
                        py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                        ${
                          activeTab === "apps_and_pods"
                            ? "border-blue-500 text-blue-600"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }
                      `}
                  >
                    Apps & PODs {"("}POD 1.0{")"} (
                    {filteredAndCategorizedServices.apps_and_pods.length})
                  </button>
                  <button
                    onClick={() => setActiveTab("plugins")}
                    className={`
                        py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                        ${
                          activeTab === "plugins"
                            ? "border-blue-500 text-blue-600"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }
                      `}
                  >
                    POD 1.0 Plugins (
                    {filteredAndCategorizedServices.plugins.length})
                  </button>
                  <button
                    onClick={() => setActiveTab("pod2_plugins")}
                    className={`
                        py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                        ${
                          activeTab === "pod2_plugins"
                            ? "border-blue-500 text-blue-600"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }
                      `}
                  >
                    POD 2.0 Plugins (
                    {filteredAndCategorizedServices.pod2_plugins.length})
                  </button>
                  <button
                    onClick={() => setActiveTab("apis")}
                    className={`
                        py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                        ${
                          activeTab === "apis"
                            ? "border-blue-500 text-blue-600"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }
                      `}
                  >
                    APIs ({filteredAndCategorizedServices.apis.length})
                  </button>
                </nav>

                {/* Search filter */}
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search services..."
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
                  />
                </div>
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-4">
              {activeTab === "apps_and_pods" &&
                renderServiceTable("apps_and_pods")}
              {activeTab === "plugins" && renderServiceTable("plugins")}
              {activeTab === "pod2_plugins" &&
                renderServiceTable("pod2_plugins")}
              {activeTab === "apis" && renderServiceTable("apis")}
            </div>
          </Card>
        )}
      </PageContainer>

      {/* Remove Service Dialog */}
      <Dialog
        open={showRemoveDialog}
        onClose={() => !isRemoving && setShowRemoveDialog(false)}
        title="Remove Service"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to remove{" "}
            <span className="font-semibold">
              {serviceToRemove?.currentVersion?.name ||
                serviceToRemove?.serviceId}
            </span>{" "}
            ({serviceToRemove?.serviceId}) from {selectedCustomer?.name}?
          </p>
          <p className="text-sm text-red-600">
            This Action cannot be undone. All Change Impact Analysis Data for
            this Service ID will be lost.
          </p>
          <div className="flex justify-end gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => setShowRemoveDialog(false)}
              disabled={isRemoving}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleConfirmRemove}
              disabled={isRemoving}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isRemoving ? "Removing..." : "Remove Service"}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ServiceUsage;
